/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Nov 16 2023
 **/

import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Maybe } from "@/frameworks/libraries/components";
import { _T } from "@rfice/language";
import { Button, Icon, Menu, Switch, Typography, useFoldingB } from "@/frameworks/designStuff/components";
import { useGroupSpaceListState, useSpaceListState } from "@/frameworks/core/chat";
import { useNotificationConfig, usePopover } from "@/frameworks/core/hooks";
import { POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { useUsers } from "@/frameworks/core/API";

import * as S from "../emotion";
import { FirstFoldingContent, SecondFoldingContent, SpaceDirectMessage } from ".";
import SpaceChatItem from "./SpaceChatItem";
import { colors } from "@/styles";
import { useChatDataStore } from "@/modules/chat/store";

// type GroupChatType = "ALONE" | "MULTI";
type SpaceChatListProps = {
	currentChatSelected: string;
	handleSelectChat: (channel: string) => void;
};

export default function SpaceChatList({ currentChatSelected, handleSelectChat }: SpaceChatListProps) {
	const queryClient = useQueryClient();
	const { data } = useNotificationConfig();
	const spaceList = useSpaceListState();
	const groupSpaceList = useGroupSpaceListState();

	const foldingB = useFoldingB();
	const userAPI = useUsers({
		onSuccess(name) {
			switch (name) {
				case "notification:update": {
					popover.hide(POPOVER_ID.CHANGE_SPACE_CHAT_ALARM);
					break;
				}
			}
		}
	});
	const popover = usePopover();
	const prioritySpaceChat = useChatDataStore((state) => state.priority_space_chat);

	const filteredGroupSpaceChat = () => {
		return groupSpaceList?.groupedSpaceChats?.sort(
			(a, b) => a?.groupName?.localeCompare(b?.groupName as string, "en", { sensitivity: "base" }) as number
		);
	};

	const FoldingBContents = useMemo(() => {
		if (prioritySpaceChat) return FirstFoldingContent;
		return SecondFoldingContent;
	}, [prioritySpaceChat]);

	const onChangeNotificationSetting = () => {
		if (data?.useSpaceChatNotification) {
			userAPI.notification.update.execute({ useSpaceChatNotification: false });
		} else {
			userAPI.notification.update.execute({ useSpaceChatNotification: true });
		}

		queryClient.setQueryData<WebServices.UserOption.GetNotificationOption.Data>(
			["notification:inquiry"],
			(prev) => {
				if (!prev) return prev;
				return {
					...prev,
					useSpaceChatNotification: !data?.useSpaceChatNotification
				};
			}
		);
	};

	const toggleChatAlarm = (ev: React.MouseEvent) => {
		popover.toggle({
			popoverID: POPOVER_ID.CHANGE_SPACE_CHAT_ALARM,
			content: (
				<Menu.Group>
					<Menu isGroup fullWidth>
						<Menu.List>
							<S.ToggleButtonCard data-option="pin" onClick={onChangeNotificationSetting}>
								{_T("전체 채팅 알림 끄기")}
								<Switch checked={!data?.useSpaceChatNotification} size="small" />
							</S.ToggleButtonCard>
						</Menu.List>
					</Menu>
				</Menu.Group>
			),
			anchorEl: ev.currentTarget,
			direction: {
				x: "left",
				y: "bottom"
			},
			zIndex: ZIndex.SIDEBAR + 1
		});
	};

	const renderGroupSpaceChat = () => {
		return (
			<>
				{filteredGroupSpaceChat()?.map((item) => {
					return (
						<SpaceChatItem
							key={item.groupId}
							item={item}
							spaceList={spaceList}
							currentChatSelected={currentChatSelected}
							handleSelectChat={handleSelectChat}
						/>
					);
				})}
			</>
		);
	};

	return (
		<FoldingBContents
			id="space-chat"
			FoldingHeaderContent={
				<S.DrawerContentHeader verticalAlign="center" horizonAlign="distribute">
					<S.SpaceChatHeader
						verticalAlign="center"
						onClick={() => foldingB.handlers.handleToggle("space-chat")}
					>
						<Maybe
							test={foldingB.state["space-chat"]?.isOpen}
							fallback={<Icon name="RightFilledBig16" size={16} />}
						>
							<Icon name="DownFilledBig16" size={16} />
						</Maybe>
						<Typography typoSize="Title3" css={{ paddingLeft: "4px" }}>
							{_T("스페이스 채팅")}
						</Typography>
					</S.SpaceChatHeader>
					<S.HeaderIconList verticalAlign="center">
						<Maybe test={!data?.useSpaceChatNotification}>
							<Typography
								typoSize="Body5_SB"
								css={{
									backgroundColor: colors.warning_00_100,
									padding: "2px 8px",
									borderRadius: "8px"
								}}
								typoColor="warning_80_100"
							>
								{_T("알림 꺼짐")}
							</Typography>
						</Maybe>
						<Button.Icon name="More24" size="large" variant="text" onClick={(ev) => toggleChatAlarm(ev)} />
					</S.HeaderIconList>
				</S.DrawerContentHeader>
			}
			FoldingBodyContent={
				<S.DirectMessageContent>
					<S.ShortDirectMessageList>
						{groupSpaceList?.myRoomChat?.rooms.map((item) => {
							return (
								<li key={item.roomId}>
									<SpaceDirectMessage
										spaceData={
											spaceList.filter(
												(item) => item.groupId === groupSpaceList?.myRoomChat?.groupId
											)[0]
										}
										currentChatSelected={currentChatSelected}
										handleSelectChat={handleSelectChat}
									/>
								</li>
							);
						})}
					</S.ShortDirectMessageList>
					{renderGroupSpaceChat()}
				</S.DirectMessageContent>
			}
		/>
	);
}
